import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import Logo from "../images/Logo.png";
import CoralReef from "../images/CoralReef.jpg";
import NetworkCanvas from "./NetworkCanvas";
import { authenticate, authenticateGoogle } from "../service/AuthService";
import { errorMessage } from "../service/HttpErrors";
import { getSignupAttribution, clearSignupAttribution } from "../service/Attribution";
import { takeConsentReturn } from "../service/OAuthService";
import { ftAccessToken, ftRefreshToken, ftUserPhoto, ftUnreadCount } from "../constants/Constants";
import { properties } from "../properties";

async function cachePhoto(accessToken) {
    try {
        const res = await fetch(properties.host + "/users/userinfo", {
            headers: { Authorization: "Bearer " + accessToken }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.imageUrl) localStorage.setItem(ftUserPhoto, data.imageUrl.replaceAll('"', ''));
    } catch { /* non-critical */ }
}

export default function Login() {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [form, setForm] = useState({ email: "", password: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [googleReady, setGoogleReady] = useState(false);
    const [pendingGoogle, setPendingGoogle] = useState(null); // holds res when new=true
    const [termsAgreed, setTermsAgreed] = useState(false);

    useEffect(() => {
        localStorage.removeItem(ftAccessToken);
        localStorage.removeItem(ftRefreshToken);
        localStorage.removeItem(ftUserPhoto);
        localStorage.removeItem(ftUnreadCount);
        let attempts = 0;
        const wait = () => {
            if (window.google?.accounts?.oauth2) { setGoogleReady(true); }
            else if (attempts++ < 100) { setTimeout(wait, 100); }
        };
        wait();
    }, []);

    const handleGoogleSignIn = () => {
        if (!window.google?.accounts?.oauth2) return;
        /* global google */
        google.accounts.oauth2.initCodeClient({
            client_id: properties.google.clientId,
            scope: "openid email profile",
            ux_mode: "popup",
            callback: (response) => {
                setLoading(true);
                // A Google sign-in from the login page can create a new account
                authenticateGoogle({ ...response, attribution: getSignupAttribution() })
                    .then(async res => {
                        if (res === 500) { setError("Google sign-in failed."); return; }
                        if (res.new) {
                            setPendingGoogle(res);
                            setLoading(false);
                        } else {
                            const token = res.authenticationResponse.access_token;
                            localStorage.setItem(ftAccessToken, JSON.stringify(token));
                            localStorage.setItem(ftRefreshToken, JSON.stringify(res.authenticationResponse.refresh_token));
                            await cachePhoto(token);
                            navigate(takeConsentReturn() || "/");
                        }
                    })
                    .catch((err) => setError(errorMessage(err, "Google sign-in failed.")))
                    .finally(() => setLoading(false));
            },
        }).requestCode();
    };

    const handleLogin = async () => {
        setError("");
        setLoading(true);
        try {
            const res = await authenticate(form.email, form.password);
            if (res === 500) {
                setError("Invalid email or password.");
                return;
            }
            localStorage.setItem(ftAccessToken, JSON.stringify(res.access_token));
            localStorage.setItem(ftRefreshToken, JSON.stringify(res.refresh_token));
            await cachePhoto(res.access_token);
            navigate(takeConsentReturn() || "/");
        } catch (err) {
            setError(errorMessage(err, "Invalid email or password."));
        } finally {
            setLoading(false);
        }
    };

    const handleTermsAccept = async () => {
        if (!termsAgreed || !pendingGoogle) return;
        setLoading(true);
        const token = pendingGoogle.authenticationResponse.access_token;
        localStorage.setItem(ftAccessToken, JSON.stringify(token));
        localStorage.setItem(ftRefreshToken, JSON.stringify(pendingGoogle.authenticationResponse.refresh_token));
        await cachePhoto(token);
        clearSignupAttribution();
        setPendingGoogle(null);
        setTermsAgreed(false);
        // Someone signing up to connect an AI app goes straight back to it; onboarding is skippable
        navigate(takeConsentReturn() || "/onboard");
    };

    const handleTermsDecline = async () => {
        if (!pendingGoogle) return;
        const token = pendingGoogle.authenticationResponse.access_token;
        setPendingGoogle(null);
        setTermsAgreed(false);
        try {
            // abandoned=true: this account was created moments ago by the Google
            // sign-in above, so the backend doesn't count it as a sign-up
            await fetch(properties.host + "/users/delete-account?abandoned=true", {
                headers: { Authorization: "Bearer " + token },
            });
        } catch { /* best effort */ }
    };

    const handleChange = (e) => { setForm({ ...form, [e.target.name]: e.target.value }); setError(""); };

    return (
        <div className="auth-layout">
            {pendingGoogle && (
                <div className="share-modal-overlay" onClick={handleTermsDecline}>
                    <div className="share-modal-tile" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, padding: 28 }}>
                        <p className="share-modal-title">One more thing</p>
                        <p style={{ fontSize: 13.5, color: "var(--ink-600)", margin: "0 0 20px", lineHeight: 1.6 }}>
                            Before we set up your account, please review and agree to our terms.
                        </p>
                        <label className="auth-agree-row" style={{ marginBottom: 24 }}>
                            <input
                                type="checkbox"
                                className="auth-agree-checkbox"
                                checked={termsAgreed}
                                onChange={e => setTermsAgreed(e.target.checked)}
                            />
                            <span className="auth-agree-text">
                                I agree to the{" "}
                                <a href="/terms" target="_blank" rel="noopener noreferrer" className="auth-link auth-link-bold">Terms of Service</a>
                                {" "}and{" "}
                                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="auth-link auth-link-bold">Privacy Policy</a>
                            </span>
                        </label>
                        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                            <button className="sub-action-btn sub-action-outline" onClick={handleTermsDecline}>
                                Cancel
                            </button>
                            <button className="auth-submit-btn" style={{ margin: 0, padding: "9px 20px", fontSize: 13.5 }} disabled={!termsAgreed || loading} onClick={handleTermsAccept}>
                                {loading ? "Setting up…" : "Continue"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Left panel */}
            <div className="auth-panel" style={{ backgroundImage: `url(${CoralReef})` }}>
                <div className="auth-panel-overlay" />
                <NetworkCanvas />
                <div className="auth-panel-content">
                    <div className="auth-logo-wrap">
                        <img src={Logo} alt="Logo" className="auth-logo" />
                    </div>
                    <h2 className="auth-panel-title">DeepThought</h2>
                    <p className="auth-panel-sub">Research analytics for the modern scientist.</p>
                </div>
            </div>

            {/* Right form */}
            <div className="auth-form-side">
                <NetworkCanvas dark />
                <div className="auth-form-wrap">
                    <h1 className="auth-heading">Welcome back</h1>
                    <p className="auth-sub">Sign in to your account to continue.</p>

                    <div className="auth-fields">
                        <div className="auth-field">
                            <label className="upload-label">Email</label>
                            <input
                                className="upload-input"
                                type="email"
                                name="email"
                                placeholder="you@example.com"
                                value={form.email}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="auth-field">
                            <div className="auth-label-row">
                                <label className="upload-label">Password</label>
                                <button className="auth-link" onClick={() => navigate("/forgot-password")}>
                                    Forgot password?
                                </button>
                            </div>
                            <div className="auth-input-wrap">
                                <input
                                    className="upload-input"
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    placeholder="••••••••"
                                    value={form.password}
                                    onChange={handleChange}
                                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                                />
                                <button
                                    className="auth-eye-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                                </button>
                            </div>
                        </div>
                    </div>

                    {error && <p className="chpw-error">{error}</p>}

                    <button className="auth-submit-btn" onClick={handleLogin} disabled={loading}>
                        {loading ? "Signing in..." : "Log In"}
                    </button>

                    <div className="auth-divider">
                        <span>or</span>
                    </div>

                    <button className="auth-google-btn" onClick={handleGoogleSignIn} disabled={!googleReady || loading}>
                        <svg width="16" height="16" viewBox="0 0 48 48">
                            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
                            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19.1 12 24 12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.3 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8H6.3C9.7 35.7 16.3 44 24 44z"/>
                            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.4 4.2-4.4 5.6l6.2 5.2C36.9 40.2 44 35 44 24c0-1.2-.1-2.4-.4-3.5z"/>
                        </svg>
                        Sign in with Google
                    </button>

                    <p className="auth-switch">
                        Don't have an account?{" "}
                        <button className="auth-link auth-link-bold" onClick={() => navigate("/register")}>
                            Create one
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
}
